<template>
  <div class="card">
    <div class="card-body p-0">
      <div v-if="error" class="alert alert-danger m-2">
        {{ error }}
      </div>

      <div v-else class="position-relative" style="aspect-ratio: 1/1;">
        <video
            ref="videoElement"
            playsinline
            class="w-100 h-100 object-cover"
            :class="{ 'scanning-disabled': !canScan }"
        ></video>

        <!-- Centered start prompt overlay -->
        <div v-if="showStartPrompt"
             class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             style="z-index: 2; background: rgba(0,0,0,0.4);">
          <div class="text-center px-3">
            <button class="btn btn-primary" @click="startScanning">{{ __DICT__.enableCamera }}</button>
            <div class="text-white-50 mt-2" style="max-width: 280px; margin: 0 auto;">
              {{ __DICT__.qrScanner.cameraPermissionMessage }}
            </div>
          </div>
        </div>

        <div class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             :class="{ 'scanning-disabled': !canScan }"
             style="background: rgba(0,0,0,0.1);">
          <div class="border border-2 border-white rounded"
               :class="{ 'scanning': canScan, 'cooldown': !canScan, 'error': lastScanWasError }"
               style="width: 20%; height: 20%;">
          </div>
        </div>

        <div v-if="!canScan"
             class="position-absolute top-50 start-50 translate-middle text-white px-3 py-2 rounded"
             style="background: rgba(0,0,0,0.7);">
          <template v-if="lastScanError">
            {{ lastScanError }}
          </template>
          <template v-else>
            {{ __DICT__.qrScanner.pleaseWait }} {{ cooldownRemaining }}{{ __DICT__.qrScanner.secondsSuffix }}
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import jsQR from 'jsqr'
import { validateBarcode } from '@/utils/scanUtils.js'
import { checkScannedCode } from '@/services/qrScan.js'
import { qrNoticeFor } from '@/utils/qrMessages.js'
import * as Sentry from '@sentry/vue'

const props = defineProps({
  cooldownPeriod: {
    type: Number,
    default: 3000 // 3 seconds cooldown
  },
  scanInterval: {
    type: Number,
    default: 1000 // Scan every 1000ms
  },
  errorDisplayTime: {
    type: Number,
    default: 3000 // How long to show error messages
  },
  allowPartial: {
    type: Boolean,
    default: false
  }
})

/**
 * code-scanned(reference, scan): reference is the bare code for local lookups;
 *   scan is { raw, reference, token, verification, mode } - send scan.raw as the
 *   code and qrRequestFields(scan) alongside it (utils/qrVerify.js).
 * qr-blocked(message, scan): enforce mode refused a bad signature locally.
 */
const emit = defineEmits(['code-scanned', 'invalid-code', 'qr-blocked'])

// Refs
const videoElement = ref(null)
const canvasElement = ref(null)
const error = ref(null)
const videoStream = ref(null)
const canScan = ref(true)
const lastScanError = ref(null)
const lastScanWasError = ref(false)
const cooldownRemaining = ref(0)
const showStartPrompt = ref(false)
const permissionState = ref('prompt') // prompt | granted | denied | unknown
let scanTimer = null
let cooldownTimer = null
let onVisibilityHandler = null

const showError = (errorMessage) => {
  lastScanError.value = errorMessage
  lastScanWasError.value = true
  canScan.value = false

  // Show error for errorDisplayTime, then reset
  setTimeout(() => {
    lastScanError.value = null
    lastScanWasError.value = false
    canScan.value = true
  }, props.errorDisplayTime)
}

const checkPermissionsAndPrepare = async () => {
  try {
    // Basic feature detection
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      error.value = 'Camera is not supported by this browser.'
      showStartPrompt.value = false
      return
    }

    // Secure context check
    const isSecure = window.isSecureContext || location.protocol === 'https:'
    if (!isSecure) {
      error.value = 'Camera access requires HTTPS connection.'
      showStartPrompt.value = false
      return
    }

    // Try to detect existing permission state
    let granted = false
    permissionState.value = 'unknown'
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const result = await navigator.permissions.query({ name: 'camera' })
        if (result && result.state) {
          permissionState.value = result.state
          granted = result.state === 'granted'
          // React to future changes (optional)
          result.onchange = () => {
            permissionState.value = result.state
          }
        }
      } catch (_) {
        // Safari may throw; ignore
        permissionState.value = 'unknown'
      }
    }

    if (granted) {
      // Auto-start if previously granted
      await startScanning()
      showStartPrompt.value = false
    } else {
      // Require explicit user gesture to start
      showStartPrompt.value = true
    }
  } catch (e) {
    // Fallback to showing the prompt if anything goes wrong
    showStartPrompt.value = true
  }
}

const startScanning = async () => {
  try {
    error.value = null

    // Add a breadcrumb to track this operation
    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'Starting camera',
      level: 'info'
    })

    // iOS devices need more conservative constraints
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const videoConstraints = isIOS
        ? {
          facingMode: 'environment',
          width: { min: 320, ideal: 720, max: 1280 },
          height: { min: 320, ideal: 720, max: 1280 }
        }
        : {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 1280 }
        }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints
    })

    videoStream.value = stream
    const video = videoElement.value

    // Guard against video element being null (component unmounted or not in DOM)
    if (!video) {
      // Clean up the stream we just acquired since we can't use it
      stream.getTracks().forEach(track => track.stop())
      videoStream.value = null
      return
    }

    // iOS inline playback requirements
    try { video.setAttribute('playsinline', 'true') } catch(_){}
    try { video.setAttribute('autoplay', '') } catch(_){}
    try { video.muted = true } catch(_){}
    video.srcObject = videoStream.value

    await video.play()

    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'Video started playing',
      level: 'info'
    })

    scanTimer = setInterval(scanQRCode, props.scanInterval)

    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'Camera started successfully',
      level: 'info'
    })
  } catch (err) {
    console.error('Camera access error:', err)

    // Handle permission denial distinctly with actionable guidance
    const msg = String(err && (err.message || err.name) || '').toLowerCase()
    if (err && (err.name === 'NotAllowedError' || msg.includes('notallowederror') || msg.includes('permission denied') || msg.includes('request is not allowed'))) {
      lastScanError.value = 'Camera permission was denied. Please enable camera access in your browser settings and try again.'
      showStartPrompt.value = true
      error.value = null // do not block UI with fatal error
      // Record as breadcrumb instead of full exception to reduce noise
      Sentry.addBreadcrumb({
        category: 'qr-scanner',
        message: 'Camera permission denied',
        level: 'warning'
      })
      return
    }

    error.value = err.message || 'Failed to access camera'

    // Capture unexpected errors in Sentry with additional context
    Sentry.captureException(err, {
      tags: {
        component: 'QrScanner',
        operation: 'startCamera'
      },
      extra: {
        userAgent: navigator.userAgent,
        isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      }
    })
  }
}

const scanQRCode = async () => {
  try {
    const video = videoElement.value
    if (!video || !canScan.value || !video.videoWidth || !video.videoHeight) return

    // Reuse the canvas and update dimensions if necessary
    if (!canvasElement.value) {
      canvasElement.value = document.createElement('canvas')
      Sentry.addBreadcrumb({
        category: 'qr-scanner',
        message: 'Canvas created',
        level: 'debug'
      })
    }

    // Ensure canvas dimensions match video and are not zero
    const width = video.videoWidth || 640
    const height = video.videoHeight || 480

    if (canvasElement.value.width !== width ||
        canvasElement.value.height !== height) {
      canvasElement.value.width = width
      canvasElement.value.height = height

      Sentry.addBreadcrumb({
        category: 'qr-scanner',
        message: 'Canvas resized',
        level: 'debug',
        data: {
          width,
          height
        }
      })
    }

    // Use a try/catch specifically for the image processing
    try {
      const context = canvasElement.value.getContext('2d', { willReadFrequently: true })
      context.drawImage(video, 0, 0, canvasElement.value.width, canvasElement.value.height)

      // On iOS, getImageData can fail if dimensions are too large
      const safeWidth = Math.min(canvasElement.value.width, 1280)
      const safeHeight = Math.min(canvasElement.value.height, 1280)
      const imageData = context.getImageData(0, 0, safeWidth, safeHeight)

      // Only proceed if we have valid image data
      if (imageData && imageData.data && imageData.width > 0 && imageData.height > 0) {
        const code = jsQR(imageData.data, imageData.width, imageData.height)

        if (code) {
          const validationResult = validateBarcode(code.data, props.allowPartial)

          if (validationResult.valid) {
            // Hold the scanner while the signature is checked, so the next
            // interval tick cannot read the same code again.
            canScan.value = false
            const { scan, decision } = await checkScannedCode(code.data)

            Sentry.addBreadcrumb({
              category: 'qr-scanner',
              message: 'Valid code scanned',
              level: 'info',
              data: {
                codeLength: code.data.length,
                codePrefix: code.data.substring(0, 10) + '...', // Don't log the full code for privacy
                qrVerification: scan.verification
              }
            })

            if (decision.block) {
              emit('qr-blocked', lastQrBlockMessage(scan), scan)
              showError(lastQrBlockMessage(scan))
              return
            }

            triggerCooldown()
            // The bare reference for the views' cached-manifest lookups; the
            // full string and verification ride along for the request body.
            emit('code-scanned', scan.reference, scan)
          } else {
            Sentry.addBreadcrumb({
              category: 'qr-scanner',
              message: 'Invalid code scanned',
              level: 'warning',
              data: {
                error: validationResult.error
              }
            })

            emit('invalid-code', validationResult.error)
            showError(validationResult.error)
          }
        }
      }
    } catch (imgError) {
      console.error('Error processing camera image:', imgError)
      Sentry.captureException(imgError, {
        tags: {
          component: 'QrScanner',
          operation: 'processImage'
        }
      })
    }
  } catch (err) {
    // Capture any errors that happen during scanning
    console.error('QR scan error:', err)

    Sentry.captureException(err, {
      tags: {
        component: 'QrScanner',
        operation: 'scanQRCode'
      },
      extra: {
        videoWidth: videoElement.value?.videoWidth,
        videoHeight: videoElement.value?.videoHeight,
        canvasPresent: !!canvasElement.value,
        canvasWidth: canvasElement.value?.width,
        canvasHeight: canvasElement.value?.height
      }
    })
  }
}

const lastQrBlockMessage = (scan) => qrNoticeFor(scan.verification, { block: true })?.text

const triggerCooldown = () => {
  canScan.value = false
  cooldownRemaining.value = Math.ceil(props.cooldownPeriod / 1000)

  cooldownTimer = setInterval(() => {
    cooldownRemaining.value--
  }, 1000)

  setTimeout(() => {
    canScan.value = true
    cooldownRemaining.value = 0
    clearInterval(cooldownTimer)
  }, props.cooldownPeriod)
}

const cleanup = () => {
  try {
    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'Cleaning up QR scanner',
      level: 'info'
    })

    if (videoStream.value) {
      const tracks = videoStream.value.getTracks()
      tracks.forEach(track => track.stop())
      videoStream.value = null
    }
    if (scanTimer) {
      clearInterval(scanTimer)
    }
    if (cooldownTimer) {
      clearInterval(cooldownTimer)
    }
    // Clean up canvas reference
    canvasElement.value = null

    Sentry.addBreadcrumb({
      category: 'qr-scanner',
      message: 'QR scanner cleanup completed',
      level: 'info'
    })
  } catch (err) {
    console.error('Error during cleanup:', err)

    Sentry.captureException(err, {
      tags: {
        component: 'QrScanner',
        operation: 'cleanup'
      }
    })
  }
}

onMounted(() => {
  checkPermissionsAndPrepare()
  onVisibilityHandler = () => {
    if (document.hidden) {
      cleanup()
    } else if (!videoStream.value) {
      if (permissionState.value === 'granted') {
        startScanning()
      } else {
        showStartPrompt.value = true
      }
    }
  }
  document.addEventListener('visibilitychange', onVisibilityHandler)
})

onBeforeUnmount(() => {
  cleanup()
  if (onVisibilityHandler) {
    document.removeEventListener('visibilitychange', onVisibilityHandler)
    onVisibilityHandler = null
  }
})
</script>

<style scoped>
.scanning-disabled {
  filter: brightness(0.3);
  transition: filter 0.3s ease;
}

.scanning {
  border-color: white !important;
  animation: scan 2s infinite;
}

.cooldown {
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.error {
  border-color: #dc3545 !important;
  animation: error-flash 0.5s;
}

@keyframes scan {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@keyframes error-flash {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
}
</style>
